import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_SIZES = [
  { value: 'school',     label: 'School (1 site)' },
  { value: 'district',   label: 'School district / trust / multi-site' },
  { value: 'university', label: 'University / higher education' },
  { value: 'sme',        label: 'Corporate · 1–500 employees' },
  { value: 'midmarket',  label: 'Corporate · 500–5,000 employees' },
  { value: 'enterprise', label: 'Corporate · 5,000+ employees' },
  { value: 'government', label: 'Government agency / public sector' },
  { value: 'partner',    label: 'Reseller / partner / integrator' },
  { value: 'press',      label: 'Press / journalist' },
  { value: 'investor',   label: 'Investor / advisor' },
  { value: 'other',      label: 'Other' },
];

const INTEREST_TIERS = [
  { value: 'b2b_plus',      label: 'HiBFF Plus for the org (paid)' },
  { value: 'pilot',         label: 'Pilot / proof-of-concept' },
  { value: 'free_eval',     label: 'Free evaluation only' },
  { value: 'partnership',   label: 'Strategic partnership' },
  { value: 'security_dpa',  label: 'Security questionnaire / DPA only' },
  { value: 'support',       label: 'Existing customer support' },
];

const ContactPage = () => {
  const [form, setForm] = useState({
    name: '', email: '', org: '', country: '', size: '', interest: '', message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errMsg, setErrMsg] = useState('');

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setErrMsg('Please fill name, email, and message.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setErrMsg('');
    try {
      const res = await fetch(`${API_URL}/api/contact/sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server returned ${res.status}`);
      }
      setStatus('sent');
    } catch (e) {
      console.error('Contact submit failed:', e);
      setErrMsg(e.message || 'Could not send. Please email business@hibff.com directly.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white" data-testid="contact-page">
      <PageMeta
        slug="contact"
        path="/contact"
        title="Talk to HiBFF"
        description="Schools, universities, corporates, government agencies, partners, press, and investors. Drop us a line — a real human will reply within 1 business day."
      />
      <header className="px-6 sm:px-12 py-6 max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900" data-testid="contact-back-home">
          <ArrowLeft size={18} /> <span className="font-medium">HiBFF home</span>
        </Link>
      </header>

      <main className="px-6 sm:px-12 max-w-4xl mx-auto pb-20">
        <div className="flex items-center gap-3 mb-2 text-purple-600">
          <Building2 size={28} />
          <span className="font-semibold text-sm uppercase tracking-wide">Contact</span>
        </div>
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-3" style={{ color: '#1A1209' }}>
          Talk to HiBFF
        </h1>
        <p className="text-base text-gray-700 mb-8 max-w-2xl">
          Schools, universities, corporates, government agencies, partners, press, and investors —
          wherever you are in the world, drop us a line and a real human will reply within{' '}
          <strong>1 business day</strong>.
        </p>

        {status === 'sent' ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center" data-testid="contact-success">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
            <h2 className="text-2xl font-semibold mb-2" style={{ color: '#1A1209' }}>Message sent ✨</h2>
            <p className="text-gray-700 mb-4">
              Thanks {form.name.split(' ')[0]} — we&apos;ve logged your enquiry and someone from the
              right team will reach out within 1 business day. A confirmation email is on its way
              to <strong>{form.email}</strong>.
            </p>
            <Link to="/" className="inline-block px-5 py-2 bg-emerald-600 text-white rounded-full font-semibold text-sm hover:bg-emerald-700">
              Back to home
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Your name *" value={form.name} onChange={v => update('name', v)} testId="contact-name" />
              <Input label="Work email *" type="email" value={form.email} onChange={v => update('email', v)} testId="contact-email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Organisation" value={form.org} onChange={v => update('org', v)} testId="contact-org" placeholder="Acme School District / Acme Co. / —" />
              <Input label="Country" value={form.country} onChange={v => update('country', v)} testId="contact-country" placeholder="United Kingdom / United States / —" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select label="Organisation type" value={form.size} onChange={v => update('size', v)} options={COMPANY_SIZES} testId="contact-size" />
              <Select label="What are you exploring?" value={form.interest} onChange={v => update('interest', v)} options={INTEREST_TIERS} testId="contact-interest" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <textarea
                value={form.message}
                onChange={e => update('message', e.target.value)}
                rows={5}
                placeholder="Tell us a bit about what you're trying to do, timelines, or any specific questions."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                data-testid="contact-message"
              />
            </div>

            {status === 'error' && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="contact-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{errMsg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              data-testid="contact-submit-btn"
              className="w-full sm:w-auto px-6 py-3 bg-purple-600 text-white rounded-full font-semibold hover:bg-purple-700 transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {status === 'sending'
                ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>)
                : (<><Send size={16} /> Send message</>)}
            </button>

            <p className="text-xs text-gray-500 pt-2">
              Prefer email? Reach us at{' '}
              <a href="mailto:business@hibff.com" className="text-purple-600 hover:underline">business@hibff.com</a>{' '}
              or{' '}
              <a href="mailto:security@hibff.com" className="text-purple-600 hover:underline">security@hibff.com</a>.
              Press enquiries: <a href="mailto:press@hibff.com" className="text-purple-600 hover:underline">press@hibff.com</a>.
            </p>
          </form>
        )}

        {status !== 'sent' && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <ContactCard icon={<Building2 size={20} />} title="Procurement / sales" email="business@hibff.com" />
            <ContactCard icon={<Mail size={20} />}      title="Security / compliance" email="security@hibff.com" />
            <ContactCard icon={<Mail size={20} />}      title="Press / journalists"   email="press@hibff.com" />
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, value, onChange, type = 'text', placeholder, testId }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
      data-testid={testId}
    />
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white"
      data-testid={testId}
    >
      <option value="">— Choose —</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const ContactCard = ({ icon, title, email }) => (
  <a href={`mailto:${email}`} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md hover:border-purple-200 transition flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-50 text-purple-600">
      {icon}
    </div>
    <div>
      <div className="font-semibold text-gray-900 text-sm">{title}</div>
      <div className="text-xs text-purple-600">{email}</div>
    </div>
  </a>
);

export default ContactPage;
